import { useReactiveGetRequest, UseReactiveGetRequestParams } from "../../request/useReactiveGetRequest";
import { AutocompleteOption, SelectInputProps } from "../../../components/SortFilter/interfaces";
import { OptionTypeVO } from "../../../types/model/valueObject/OptionTypeVO";
import { useGetOptions, UseGetOptionsConditionalParams, VariableInputOptionType, OptionType } from "../../report/useGetOptions";

export type ServerResponseType = string | AutocompleteOption | SelectInputProps | OptionTypeVO;
export interface UseGetRequestOptionsParams extends UseGetOptionsConditionalParams, UseReactiveGetRequestParams {
}

/**
	 * Performs a reactive GET request and normalizes the server response into a list of `SelectInputProps`s.
	 * The hook supports response items as VariableInputOptionType, relying on the useGetOptions hook to handle
	 * the normalization logic. It also prepends a default "All" option to the list of options.
	 *
	 * @param useGetRequestOptionsParams - Configuration for the underlying `useReactiveGetRequest` hook
	 * (e.g. URL, query parameters, and other request options).
	 * @returns An object containing:
	 * - `options`: an array of normalized `AutocompleteOption` or 'SelectInputProps' items
	 * - `isLoading`: a boolean indicating whether the request is currently in progress;
	 * - `error`: any error returned by the underlying request, or `undefined`/`null` if none.
	 */

const useGetRequestOptions = (useGetRequestOptionsParams: UseGetRequestOptionsParams) => {
  const {
    //UseGetOptionsConditionalParams: needed for useGetOptions
    optionFormat,
    includeAllOption,

    //UseReactiveGetRequestParams needed for useReactiveGetRequest
    requestUrl,
    queryParams,
    shouldAbort
  } = useGetRequestOptionsParams;

  const {
    data,
    isLoading,
    error,
  } = useReactiveGetRequest<ServerResponseType[]>({ requestUrl, queryParams, shouldAbort });

  const options = useGetOptions({
    optionFormat,
    includeAllOption,
    options: (data || []) as unknown as VariableInputOptionType
  }) as unknown as OptionType[];

  return {
    options,
    isLoading,
    error,
  }
}

export { useGetRequestOptions };
